<!-- File Preview Overlay — the root takes focus on open (never a tab stop) so the opener's key handlers stand down. -->
@if (previewService.isOpen()) {
  <div class="preview-overlay" #overlayRoot tabindex="-1" (click)="onOverlayClick($event)"
       animate.enter="preview-overlay-enter" animate.leave="preview-overlay-leave">
    <div class="preview-container">
      <!-- Toolbar -->
      <div class="preview-toolbar">
        <div class="toolbar-left">
          <button class="toolbar-btn back-btn" (click)="close()" [pTooltip]="'common.close' | translate">
            <fa-duotone-icon [icon]="faArrowLeft"></fa-duotone-icon>
            <span class="btn-label">{{ 'common.back' | translate }}</span>
          </button>

          @if (previewService.item(); as item) {
            <span class="toolbar-filename" [title]="item.name">{{ item.name }}</span>
          }
          <!-- The AI disclosure, for every reader, off the item's own origin (the tile's chip, carried into the preview). -->
          @if (headerAiDisclosure(); as d) {
            <app-ai-disclosure-chip [disclosure]="d" [inline]="true" />
          }
        </div>

        <!-- Center zone: the zoom segment, truly centered between the identity (left) and
             curation (right) zones. The percent readout doubles as a preset dropdown —
             step with −/+ or jump straight to a level (Fit / 50 / 100 / 200 / 400). -->
        <div class="toolbar-center">
          @if (zoomControlsVisible()) {
            <button class="toolbar-btn" (click)="zoomOut()" [pTooltip]="'filePreview.zoomOut' | translate">
              <fa-duotone-icon [icon]="faMinus"></fa-duotone-icon>
            </button>
            <button class="toolbar-btn toolbar-zoom-trigger" (click)="zoomMenu.toggle($event)">
              {{ zoomPercent() }}
              <fa-duotone-icon [icon]="faChevronDown" class="zoom-caret"></fa-duotone-icon>
            </button>
            <p-menu #zoomMenu [model]="zoomPresetItems()" [popup]="true" appendTo="body" styleClass="zoom-preset-menu"></p-menu>
            <button class="toolbar-btn" (click)="zoomIn()" [pTooltip]="'filePreview.zoomIn' | translate">
              <fa-duotone-icon [icon]="faPlus"></fa-duotone-icon>
            </button>
          }
        </div>

        <div class="toolbar-right">
          <!-- Curation zone. The zoom segment lives in the CENTER zone; the workflow
               gestures (annotate, pin-to-compare) live in the bottom control row beside
               the context's action bar — a reviewer's tools sit where the workflow lives,
               not among the generic curation icons. -->
          <!-- Bookmark + Favorite: writes — hidden in "Time travel" -->
          @if (!temporal.active()) {
            <!-- Bookmark — personal member state: hidden for read-only monitoring roots and for non-members. -->
            @if (!access.isReadOnlyRoot() && isMember()) {
            <!-- Three states: bookmarked · not · UNKNOWN (the list could not load — a neutral, disabled star). -->
            <button class="toolbar-btn"
              [class.active]="isItemBookmarked() === true"
              [disabled]="isItemBookmarked() === undefined"
              (click)="toggleBookmark()"
              [pTooltip]="bookmarkLabelKey(isItemBookmarked(), bookmarkService.unknownReason()) | translate">
              <fa-duotone-icon [icon]="faBookmark"></fa-duotone-icon>
            </button>
            }

            <!-- Featured — brand-content curation write: hidden for read-only monitoring roots and
                 for non-members (the server blocks the mutation anyway; showing the button would be a
                 lying affordance). A TOGGLE with three states: featured (lit) · not · UNKNOWN (the
                 full-item read has not landed — a neutral, disabled star; the bookmark's shape). The
                 tooltip names the act. -->
            @if (!access.isReadOnlyRoot() && isMember()) {
            <button class="toolbar-btn"
              [class.active]="featuredState() === true"
              [disabled]="featuredState() === undefined || featuredBusy()"
              (click)="toggleFeatured()"
              [pTooltip]="featuredLabelKey() | translate">
              <fa-duotone-icon [icon]="featuredState() === true ? faStarSolid : faStar"></fa-duotone-icon>
            </button>
            }
          }

          <!-- Share — deliberately OUTSIDE the time-travel gate: sharing the past view is a
               now-gesture by ruling (the link carries the viewed instant). Still a write
               affordance (it grants access), so read-only roots and non-members don't see it. -->
          @if (!access.isReadOnlyRoot() && isMember()) {
          <button class="toolbar-btn share-btn" (click)="openShareDialog()"
            [pTooltip]="'share.action' | translate">
            <fa-duotone-icon [icon]="faShareNodes"></fa-duotone-icon>
          </button>
          }

          <!-- Panel toggle -->
          <button
            class="toolbar-btn"
            [class.active]="!previewService.panelCollapsed()"
            (click)="previewService.togglePanel()"
            [pTooltip]="'filePreview.toggleInfo' | translate">
            <fa-duotone-icon [icon]="faSidebarFlip"></fa-duotone-icon>
          </button>
        </div>
      </div>

      <!-- Main content area -->
      <div class="preview-main">
        <!-- Compare-mode vs single-pane content is mutually exclusive. Using @if/@else
             ensures only one subtree mounts — avoids double-rendered layouts where both
             the compare-container and the preview-content were visible simultaneously. -->
        @if (previewService.isCompareMode() && previewService.context()?.compare?.enabled) {
          <div class="compare-container"
               [attr.data-layout]="previewService.compareLayout()"
               [class.show-pane-focus]="showPaneFocus()">
            @for (paneId of orderedPinnedIds(); track paneId; let i = $index) {
              <div class="compare-pane"
                   [class.focused]="focusedPaneIndex() === i"
                   (click)="setFocusedPane(i)"
                   [attr.data-pane-index]="i">
                @let paneItem = itemById(paneId);
                @if (paneItem) {
                  <div class="compare-pane-image" [style.background-image]="'url(' + getPreviewUrl(paneItem) + ')'">
                    <span class="compare-pane-label">{{ paneLabelFor(paneItem) || ('filePreview.compare.paneLabel' | translate: { n: i + 1 }) }}</span>
                    <button class="compare-pane-unpin"
                            (click)="unpinPane(paneId, $event)"
                            [attr.aria-label]="'filePreview.compare.unpin' | translate">
                      <fa-duotone-icon [icon]="faCircleMinus"></fa-duotone-icon>
                    </button>
                  </div>
                  @if (previewService.context()?.actionBar?.component && previewService.context()?.actionBar?.mode === 'per-pane') {
                    <div class="compare-pane-actionbar">
                      <ng-container *ngComponentOutlet="previewService.context()!.actionBar!.component;
                          inputs: actionBarInputsForPane(paneItem)"></ng-container>
                    </div>
                  }
                }
              </div>
            }
            @if (globalActionBar()) {
              <!-- ONE container-level bar spanning every column, in an implicit auto row
                   below the panes — the verdict's subject is the whole review, not a pane. -->
              <div class="compare-global-actionbar">
                <ng-container *ngComponentOutlet="previewService.context()!.actionBar!.component;
                    inputs: actionBarInputsForPane(previewService.item())"></ng-container>
              </div>
            }
          </div>
        } @else {
        <!-- Content viewer (single-pane mode). Column flex: viewer-area (flex:1) + optional
             actionbar (flex:0 0 auto) below. The actionbar lives inside preview-content so
             it shares the content column's width (not the info-panel sidebar), and so single-
             pane structurally mirrors compare-pane (each pane = image + actionbar). -->
        <div class="preview-content">
          <div class="viewer-area" (wheel)="onWheel($event)">
          <!-- Navigation arrows -->
          @if (previewService.canNavigatePrev()) {
            <button class="nav-arrow nav-prev" (click)="previewService.prev()" [pTooltip]="'contentList.previous' | translate">
              <fa-duotone-icon [icon]="faChevronLeft"></fa-duotone-icon>
            </button>
          }

          @if (previewService.canNavigateNext()) {
            <button class="nav-arrow nav-next" (click)="previewService.next()" [pTooltip]="'contentList.next' | translate">
              <fa-duotone-icon [icon]="faChevronRight"></fa-duotone-icon>
            </button>
          }

          <!-- The annotation pins' read failed: a compact face over the stage, for the image, the video
               and the PDF alike — never media that merely looks un-annotated. -->
          @if (annotationsLoadFailed()) {
            <div class="annotation-load-failed">
              <app-load-failed [compact]="true" [message]="'filePreview.annotationsLoadFailed' | translate" [retrying]="annotationsReloading()" (retry)="retryAnnotationComments()"/>
            </div>
          }

          <!-- Image viewer -->
          @if (previewService.contentType() === 'image') {
            @switch (pdfRenderState()) {
              @case ('loading') {
                <div class="loading-state">
                  <p-progressSpinner strokeWidth="3"></p-progressSpinner>
                  <p>{{ 'common.loading' | translate }}</p>
                </div>
              }
              @case ('uploading') {
                <div class="empty-state">
                  <fa-duotone-icon [icon]="faHourglass" class="empty-icon"></fa-duotone-icon>
                  <h2>{{ 'filePreview.uploading.title' | translate }}</h2>
                  <p>{{ 'filePreview.uploading.message' | translate }}</p>
                </div>
              }
              @case ('awaiting') {
                <div class="empty-state">
                  <fa-duotone-icon [icon]="faHourglass" class="empty-icon"></fa-duotone-icon>
                  <h2>{{ 'filePreview.pending.title' | translate }}</h2>
                  <p>{{ 'filePreview.pending.message' | translate }}</p>
                </div>
              }
              @case ('processing') {
                <div class="empty-state">
                  <fa-duotone-icon [icon]="faSpinner" class="fa-spin empty-icon"></fa-duotone-icon>
                  <h2>{{ 'filePreview.processing.title' | translate }}</h2>
                  <p>{{ 'filePreview.processing.message' | translate }}</p>
                </div>
              }
              @case ('failed') {
                <div class="empty-state">
                  <fa-duotone-icon [icon]="faTriangleExclamation" class="empty-icon"></fa-duotone-icon>
                  <h2>{{ 'filePreview.failed.title' | translate }}</h2>
                  <p>{{ 'filePreview.failed.message' | translate }}</p>
                </div>
              }
              @case ('ready') {
                <div class="viewer-image" #viewerImage [class.annotating]="annotateMode()">
                  <img
                    [src]="imageUrl()"
                    [alt]="previewService.item()?.name ?? ''"
                    [style.transform]="imageTransform()"
                    [class.pannable]="zoom() > 1 && !annotateMode()"
                    [class.panning]="isPanning()"
                    (load)="onImageLoad($event)"
                    (pointerdown)="onImagePointerDown($event)"
                    (pointermove)="onImagePointerMove($event)"
                    (pointerup)="onImagePointerUp($event)"
                    (pointercancel)="onImagePointerCancel($event)"
                    draggable="false" />

                  <!-- Annotation overlay: numbered pins projected over the image. The layer is
                       pointer-transparent (the img underneath owns pan AND the annotate gesture);
                       positions ride the coordinate mapping, so pins track zoom + pan without the
                       layer itself transforming. Pins are comments: a public visitor reads none,
                       so the layer never mounts in public mode. -->
                  @if (!publicMode()) {
                  <div class="annotation-overlay">
                    @for (pin of annotationPins(); track pin.id) {
                      @if (pin.kind === 'REGION') {
                        <div class="annotation-region"
                             [style.left.px]="pin.x" [style.top.px]="pin.y"
                             [style.width.px]="pin.width" [style.height.px]="pin.height"></div>
                      }
                      <button type="button" class="annotation-pin"
                              [class.active]="previewService.activeAnnotationId() === pin.id"
                              [style.left.px]="pin.x" [style.top.px]="pin.y"
                              (click)="onOverlayPinClick(pin.id)"
                              (mouseenter)="onOverlayPinHover(pin.id, true)"
                              (mouseleave)="onOverlayPinHover(pin.id, false)"
                              [attr.aria-label]="'comments.showAnnotation' | translate: { n: pin.ordinal }">
                        {{ pin.ordinal }}
                      </button>
                    }
                    @if (annotationDraftRect(); as draft) {
                      <div class="annotation-draft"
                           [style.left.px]="draft.left" [style.top.px]="draft.top"
                           [style.width.px]="draft.width" [style.height.px]="draft.height"></div>
                    }
                    <!-- The captured-but-not-yet-posted mark stays visible while the comment
                         is written (draft-styled, unnumbered — the ordinal arrives on post).
                         It clears together with the composer's chip: post, dismiss, Escape,
                         annotate-off, or navigation. -->
                    @if (pendingAnnotationPin(); as pending) {
                      @if (pending.kind === 'REGION') {
                        <div class="annotation-region annotation-region--pending"
                             [style.left.px]="pending.x" [style.top.px]="pending.y"
                             [style.width.px]="pending.width" [style.height.px]="pending.height"></div>
                      }
                      <span class="annotation-pin annotation-pin--pending"
                            [style.left.px]="pending.x" [style.top.px]="pending.y">
                        <fa-duotone-icon [icon]="faLocationDot"></fa-duotone-icon>
                      </span>
                    }
                  </div>
                  }
                </div>
              }
            }
          }

          <!-- Video viewer -->
          @if (previewService.contentType() === 'video') {
            @switch (pdfRenderState()) {
              @case ('loading') {
                <div class="loading-state">
                  <p-progressSpinner strokeWidth="3"></p-progressSpinner>
                  <p>{{ 'common.loading' | translate }}</p>
                </div>
              }
              @case ('uploading') {
                <div class="empty-state">
                  <fa-duotone-icon [icon]="faHourglass" class="empty-icon"></fa-duotone-icon>
                  <h2>{{ 'filePreview.uploading.title' | translate }}</h2>
                  <p>{{ 'filePreview.uploading.message' | translate }}</p>
                </div>
              }
              @case ('awaiting') {
                <div class="empty-state">
                  <fa-duotone-icon [icon]="faHourglass" class="empty-icon"></fa-duotone-icon>
                  <h2>{{ 'filePreview.pending.title' | translate }}</h2>
                  <p>{{ 'filePreview.pending.message' | translate }}</p>
                </div>
              }
              @case ('processing') {
                <div class="empty-state">
                  <fa-duotone-icon [icon]="faSpinner" class="fa-spin empty-icon"></fa-duotone-icon>
                  <h2>{{ 'filePreview.processing.title' | translate }}</h2>
                  <p>{{ 'filePreview.processing.message' | translate }}</p>
                </div>
              }
              @case ('failed') {
                <div class="empty-state">
                  <fa-duotone-icon [icon]="faTriangleExclamation" class="empty-icon"></fa-duotone-icon>
                  <h2>{{ 'filePreview.failed.title' | translate }}</h2>
                  <p>{{ 'filePreview.failed.message' | translate }}</p>
                </div>
              }
              @case ('ready') {
                <div class="viewer-video">
                  @if (videoLoading()) {
                    <div class="loading-state">
                      <p-progressSpinner strokeWidth="3"></p-progressSpinner>
                      <p>{{ 'filePreview.loadingVideo' | translate }}</p>
                    </div>
                  } @else if (videoData()) {
                    <app-video-player
                      [videoData]="videoData()!"
                      [itemId]="previewService.item()?.id ?? ''"
                      [posterUrl]="getPreviewUrl(previewService.item())"
                      [markers]="videoTimecodeMarkers()"
                      [annotateMode]="annotateMode()"
                      [framePins]="videoFramePins()"
                      [pendingFramePin]="pendingVideoFramePin()"
                      [activePinId]="previewService.activeAnnotationId()"
                      (annotationCapture)="onVideoAnnotationCapture($event)"
                      (framePinClick)="onOverlayPinClick($event)">
                    </app-video-player>
                  } @else {
                    <div class="video-placeholder focus-ring" role="button" tabindex="0"
                         [attr.aria-label]="previewService.item()?.name ?? ''"
                         (click)="loadVideo()" (keydown.enter)="loadVideo()">
                      <img [src]="getPreviewUrl(previewService.item())" [alt]="previewService.item()?.name ?? ''" />
                      <div class="play-overlay">
                        <fa-duotone-icon [icon]="faPlay" class="play-icon"></fa-duotone-icon>
                      </div>
                    </div>
                  }
                </div>
              }
            }
          }

          <!-- PDF viewer -->
          @if (previewService.contentType() === 'pdf') {
            <div class="viewer-pdf">
              @switch (pdfRenderState()) {
                @case ('loading') {
                  <div class="loading-state">
                    <p-progressSpinner strokeWidth="3"></p-progressSpinner>
                    <p>{{ 'common.loading' | translate }}</p>
                  </div>
                }
                @case ('uploading') {
                  <div class="empty-state">
                    <fa-duotone-icon [icon]="faHourglass" class="empty-icon"></fa-duotone-icon>
                    <h2>{{ 'filePreview.uploading.title' | translate }}</h2>
                    <p>{{ 'filePreview.uploading.message' | translate }}</p>
                  </div>
                }
                @case ('awaiting') {
                  <div class="empty-state">
                    <fa-duotone-icon [icon]="faHourglass" class="empty-icon"></fa-duotone-icon>
                    <h2>{{ 'filePreview.pending.title' | translate }}</h2>
                    <p>{{ 'filePreview.pending.message' | translate }}</p>
                  </div>
                }
                @case ('processing') {
                  <div class="empty-state">
                    <fa-duotone-icon [icon]="faSpinner" class="fa-spin empty-icon"></fa-duotone-icon>
                    <h2>{{ 'filePreview.processing.title' | translate }}</h2>
                    <p>{{ 'filePreview.processing.message' | translate }}</p>
                  </div>
                }
                @case ('failed') {
                  <div class="empty-state">
                    <fa-duotone-icon [icon]="faTriangleExclamation" class="empty-icon"></fa-duotone-icon>
                    <h2>{{ 'filePreview.failed.title' | translate }}</h2>
                    <p>{{ 'filePreview.failed.message' | translate }}</p>
                  </div>
                }
                @case ('ready') {
                  @if (documentUrl()) {
                    <!-- The PDF engine (pdfjs + the viewer) is a lazily loaded chunk: it arrives the
                         moment a PDF is previewed, never at boot, so the library's browser floor is
                         the preview's floor and not the application's. A load that fails stays failed
                         for this page (Angular's deferred loading is one-shot per template), so Try
                         again reloads the page; the URL bridge brings this preview back. -->
                    @defer (on immediate) {
                      <app-pdf-viewer
                        #pdfViewer
                        [src]="documentUrl()"
                        [height]="'100%'"
                        [keyboardScope]="true"
                        (pdfLoadStart)="pdfReadyUrl.set(null)"
                        (pdfLoaded)="pdfReadyUrl.set(documentUrl())"
                        (pdfLoadError)="pdfReadyUrl.set(null)"
                        [showToolbar]="true"
                        [showThumbnails]="true"
                        [pageAnnotations]="pdfAnnotationPins()"
                        [pendingAnnotation]="pendingPdfAnnotation()"
                        [annotateMode]="annotateMode()"
                        (annotationClick)="onPdfAnnotationClick($event)"
                        (annotationPinClick)="onOverlayPinClick($event)">
                      </app-pdf-viewer>
                    } @loading (after 150ms) {
                      <div class="loading-state">
                        <p-progressSpinner strokeWidth="3"></p-progressSpinner>
                        <p>{{ 'common.loading' | translate }}</p>
                      </div>
                    } @error {
                      @if (pdfEngineSupported) {
                        <app-load-failed [compact]="true" [message]="'filePreview.pdfPreviewUnavailable.message' | translate" (retry)="reloadForPdfEngine()"/>
                      } @else {
                        <!-- The engine cannot run on this browser at all; a retry would never work, so none is offered. -->
                        <div class="empty-state">
                          <fa-duotone-icon [icon]="faBrowser" class="empty-icon"></fa-duotone-icon>
                          <p>{{ 'filePreview.pdfPreviewUnsupported.message' | translate }}</p>
                        </div>
                      }
                    }
                  } @else {
                    <!-- documentUrl arrives via fetchItemFull moments after open(); show a
                         spinner rather than guess at a server inconsistency. -->
                    <div class="loading-state">
                      <p-progressSpinner strokeWidth="3"></p-progressSpinner>
                      <p>{{ 'common.loading' | translate }}</p>
                    </div>
                  }
                }
              }
            </div>
          }

          <!-- Article viewer -->
          @if (previewService.contentType() === 'article') {
            @if (previewService.item(); as item) {
              <div class="viewer-article">
                <div class="article-content">
                  <!-- The one body-state voice the three article doors share: the headline is the one editorial
                       read label; a body that exists only in another language names the missing language, never
                       an empty page; the foreign body itself is never shown (strict-editorial). -->
                  <h1 class="article-title">{{ articleHeadline() }}</h1>
                  @switch (articleState()) {
                    @case ('content') {
                      <div class="article-text" [innerHTML]="articleBody() | safeHtml"></div>
                    }
                    @case ('untranslated') {
                      <!-- The whole missing-translation state (the sentence naming the missing language, where the
                           body is available, the switch hint); the preview is a reading surface, so no Translate door.
                           This overlay inerts the top bar, so the hint says to close the view first. -->
                      <app-untranslated-state field="body" [value]="item.text" [topBarReachable]="false" />
                    }
                    @default {
                      <p class="article-placeholder">{{ 'manual.emptyArticle' | translate }}</p>
                    }
                  }
                </div>
              </div>
            }
          }

          <!-- Loading state, else the error face — one chain: a read in flight never shows a stale face. -->
          @if (previewService.loading()) {
            <div class="loading-state">
              <p-progressSpinner strokeWidth="3"></p-progressSpinner>
              <p>{{ 'common.loading' | translate }}</p>
            </div>
          } @else if (previewService.error(); as error) {
            <!-- The canonical section-grain face; the sentence arrives pre-translated. -->
            <app-load-failed [compact]="true" [message]="error" (retry)="loadVideo()"/>
          } @else if (previewService.itemLoadFailed()) {
            <!-- The full-item read behind the preview failed: the listing item frames the viewer, and
                 this face says the details didn't come — never a thinner file passed off as the whole. -->
            <app-load-failed [compact]="true" [message]="'filePreview.itemLoadError' | translate" (retry)="previewService.reloadFullItem()"/>
          }
          </div>

          <!-- Bottom control row (directly below the viewer, inside preview-content so it
               shares the content column width): the viewer tools — annotate + pin-to-compare,
               promoted from the generic top toolbar so they sit beside the workflow controls
               where a reviewer looks — plus the context's actionBar slot. Compare mode renders
               its own per-pane actionbar inside each compare-pane. -->
          @if (previewService.item() && (viewerToolsVisible() || previewService.context()?.actionBar?.component)) {
            <div class="preview-actionbar">
              @if (viewerToolsVisible()) {
                <div class="viewer-tools">
                  <!-- Annotate toggle — arms the point/region gesture on the image (or PDF page). -->
                  @if (annotateAvailable()) {
                    <button class="toolbar-btn"
                      [class.active]="annotateMode()"
                      (click)="toggleAnnotateMode()">
                      <fa-duotone-icon [icon]="faLocationDot"></fa-duotone-icon>
                      <span class="btn-label">{{ 'filePreview.annotate' | translate }}</span>
                    </button>
                  }

                  <!-- Pin to compare — visible when the context enables compare mode (Selects,
                       Reviews). Pins the currently-visible item; a second click unpins. When
                       ≥ 2 items are pinned, the preview switches to multi-pane compare. -->
                  @if (compareEnabled()) {
                    <button class="toolbar-btn"
                      [class.active]="currentItemPinned()"
                      [disabled]="!currentItemPinned() && !previewService.canPinMore()"
                      (click)="togglePinCurrent()"
                      [pTooltip]="pinTooltipKey() | translate">
                      <fa-duotone-icon [icon]="faThumbtack"></fa-duotone-icon>
                      <span class="btn-label">{{ (currentItemPinned() ? 'filePreview.compare.unpin' : 'filePreview.compare.pin') | translate }}</span>
                    </button>
                  }
                </div>
              }
              @if (previewService.context()?.actionBar?.component) {
                <div class="actionbar-slot">
                  <ng-container *ngComponentOutlet="previewService.context()!.actionBar!.component;
                      inputs: actionBarInputsForPane(previewService.item()!)"></ng-container>
                </div>
              }
            </div>
          }
        </div>
        }

        <!-- Info panel (Desktop) -->
        @if (!previewService.panelCollapsed() && !isMobile()) {
          <div class="preview-info-panel" animate.enter="preview-info-panel-enter" animate.leave="preview-info-panel-leave">
            @if (previewService.item(); as item) {
              <app-preview-info
                [item]="item"
                [downloadOptions]="downloadOptions()"
                [canEdit]="previewService.canEdit()"
                [contentType]="previewService.contentType()"
                [canPost]="effectiveCanPost()"
                [canModerate]="effectiveCanModerate()"
                [anchorsNavigable]="threadArmed()"
                (download)="download($event)"
                (itemUpdated)="onItemUpdated($event)">
              </app-preview-info>
            }
          </div>
        }
      </div>

      <!-- Mobile Bottom Sheet -->
      @if (isMobile() && !previewService.panelCollapsed()) {
        <div
          #bottomSheet
          class="mobile-bottom-sheet"
          [class.expanded]="bottomSheetExpanded()"
          animate.enter="mobile-bottom-sheet-enter"
          animate.leave="mobile-bottom-sheet-leave">
          <!-- Drag handle -->
          <div
            class="bottom-sheet-handle focus-ring"
            role="button" tabindex="0"
            [attr.aria-label]="'filePreview.details' | translate"
            [attr.aria-expanded]="bottomSheetExpanded()"
            (touchstart)="onBottomSheetDragStart($event)"
            (touchmove)="onBottomSheetDrag($event)"
            (touchend)="onBottomSheetDragEnd()"
            (mousedown)="onBottomSheetDragStart($event)"
            (click)="toggleBottomSheet()"
            (keydown.enter)="toggleBottomSheet()">
            <div class="handle-bar"></div>
          </div>

          <!-- Content -->
          <div class="bottom-sheet-content">
            @if (previewService.item(); as item) {
              <app-preview-info
                [item]="item"
                [downloadOptions]="downloadOptions()"
                [canEdit]="previewService.canEdit()"
                [contentType]="previewService.contentType()"
                [canPost]="effectiveCanPost()"
                [canModerate]="effectiveCanModerate()"
                [anchorsNavigable]="threadArmed()"
                [compact]="!bottomSheetExpanded()"
                (download)="download($event)"
                (itemUpdated)="onItemUpdated($event)">
              </app-preview-info>
            }
          </div>
        </div>
      }

      <!-- Filmstrip (Desktop only). In compare-enabled contexts, each thumbnail carries a
           pin badge in its corner — currently-pinned items show the badge filled, and hover
           reveals it on un-pinned items. -->
      @if (previewService.showFilmstrip() && !isMobile()) {
        <div class="preview-filmstrip">
          <div class="filmstrip-track">
            @for (item of previewService.items(); track item.id; let i = $index) {
              <button
                class="filmstrip-item"
                [class.active]="i === previewService.currentIndex()"
                [class.pinned]="previewService.pinnedIds().has(item.id)"
                (click)="onFilmstripItemClick(i)">
                @if (showThumbnailFallback(item)) {
                  <span class="filmstrip-fallback">
                    <fa-duotone-icon [icon]="getItemIcon(item)" [style.color]="getItemIconColor(item)"></fa-duotone-icon>
                  </span>
                } @else {
                  <img [src]="getThumbnailUrl(item)" [alt]="item.name" (error)="onThumbnailError(item)" />
                }
                @if (compareEnabled()) {
                  <span class="filmstrip-pin"
                        (click)="togglePinFilmstrip(item, $event)"
                        [pTooltip]="(previewService.pinnedIds().has(item.id)
                            ? 'filePreview.compare.unpin'
                            : 'filePreview.compare.pin') | translate">
                    <fa-duotone-icon [icon]="faThumbtack"></fa-duotone-icon>
                  </span>
                }
              </button>
            }
          </div>
          <span class="filmstrip-position">{{ 'filePreview.position' | translate:{ current: previewService.currentIndex() + 1, total: previewService.itemCount() } }}</span>
        </div>
      }

      <!-- Dots Navigation (Mobile only) -->
      @if (previewService.showFilmstrip() && isMobile()) {
        <div class="mobile-dots-nav">
          <div class="dots-container">
            @for (dotIndex of getVisibleDotsRange(); track dotIndex) {
              <button
                class="nav-dot"
                [class.active]="dotIndex === previewService.currentIndex()"
                [class.edge]="dotIndex === getVisibleDotsRange()[0] || dotIndex === getVisibleDotsRange()[getVisibleDotsRange().length - 1]"
                (click)="onDotClick(dotIndex)">
              </button>
            }
          </div>
          <span class="dots-position">{{ 'filePreview.position' | translate:{ current: previewService.currentIndex() + 1, total: previewService.itemCount() } }}</span>
        </div>
      }
    </div>
  </div>
}
